import Element from "state/ui/editor/elements/Element";
import {
  getLineBoundingBox,
  calculateLineCenter,
  moveLineCoordinates,
  calculateLinePadding,
  scaleLineCoordinates
} from "views/components/Editor/elements/Line/lineCoordinateUtils";

class LineElement extends Element {
  static RESTRICTIONS = [
    "removable",
    "visibility",
    "opacity",
    "duplicate",
    "sizeAndPosition",
    "angle",
    "color",
    "outline",
    "flipping",
    "animations",
    "lineStyle"
  ];

  static DEFAULT_PROPS = {};

  constructor(props) {
    super(props);

    // Initialize bounding box properties from coordinates if not provided.
    // This ensures LineElement instances always have left/top/width/height
    // which are required by getElementsBox and calculateSelectionCentroid
    // for multi-element selection.
    if (
      this.coordinates &&
      (this.left === undefined || this.top === undefined)
    ) {
      const { x1, y1, x2, y2 } = this.coordinates;
      const padding = LineElement.calculatePadding(
        this.thickness || 1,
        this.endcap
      );
      const bbox = getLineBoundingBox(x1, y1, x2, y2, padding);
      this.left = bbox.left;
      this.top = bbox.top;
      this.width = bbox.width;
      this.height = bbox.height;
    }
  }

  /**
   * Calculate padding for the bounding box, including endcap space if needed.
   * @param {number} thickness - Line thickness
   * @param {object} endcap - Endcap configuration
   * @returns {number} Total padding
   */
  static calculatePadding(thickness, endcap) {
    return calculateLinePadding(thickness, endcap);
  }

  /**
   * Get the bounding box for this line element.
   * Calculates from the line's endpoint coordinates.
   * @returns {{ top: number, left: number, width: number, height: number }}
   */
  get boundingBox() {
    const { x1, y1, x2, y2 } = this.coordinates;
    // Add padding based on stroke thickness and endcaps for accurate bounding box
    const padding = LineElement.calculatePadding(
      this.thickness || 1,
      this.endcap
    );
    return getLineBoundingBox(x1, y1, x2, y2, padding);
  }

  /**
   * Get the center point of this line.
   * @returns {{ x: number, y: number }}
   */
  get center() {
    const { x1, y1, x2, y2 } = this.coordinates;
    return calculateLineCenter(x1, y1, x2, y2);
  }

  getRestrictionsMap() {
    const restrictions = {};

    let availableRestrictions = [...this.constructor.RESTRICTIONS];

    availableRestrictions.forEach(
      restriction => (restrictions[restriction] = false)
    );

    this.restrictions.forEach(restriction => {
      restrictions[restriction] = true;
    });

    return restrictions;
  }

  updateAttributes(attributes) {
    // If coordinates, endcap, or thickness are being updated, recalculate bounding box properties
    // Thickness affects padding calculation, so bounding box must be recalculated
    if (
      attributes.coordinates ||
      attributes.endcap ||
      attributes.thickness !== undefined
    ) {
      const coordinates = attributes.coordinates || this.coordinates;
      const { x1, y1, x2, y2 } = coordinates;
      const thickness = attributes.thickness ?? this.thickness ?? 1;
      const endcap = attributes.endcap || this.endcap;
      const padding = LineElement.calculatePadding(thickness, endcap);
      const bbox = getLineBoundingBox(x1, y1, x2, y2, padding);

      return new this.constructor({
        ...this,
        ...attributes,
        top: bbox.top,
        left: bbox.left,
        width: bbox.width,
        height: bbox.height
      });
    }

    return new this.constructor({
      ...this,
      ...attributes
    });
  }

  /**
   * Move the line by a delta.
   * Updates both endpoints to preserve line shape.
   * @param {{ top: number, left: number }} delta - Movement delta
   * @returns {LineElement} New LineElement instance with updated position
   */
  move({ top, left }) {
    const newCoordinates = moveLineCoordinates(this.coordinates, left, top);
    const padding = LineElement.calculatePadding(
      this.thickness || 1,
      this.endcap
    );
    const bbox = getLineBoundingBox(
      newCoordinates.x1,
      newCoordinates.y1,
      newCoordinates.x2,
      newCoordinates.y2,
      padding
    );

    return new this.constructor({
      ...this,
      coordinates: newCoordinates,
      top: bbox.top,
      left: bbox.left,
      width: bbox.width,
      height: bbox.height
    });
  }

  /**
   * Resize the line by scaling coordinates relative to an anchor point.
   * Overrides Element.resize() to handle coordinate-based positioning.
   * @param {object} params - Resize parameters from Design.resizeElements()
   * @param {number} params.scale - Scale factor
   * @param {{ x: number, y: number }} params.anchorPoint - Fixed point for scaling (in screen coordinates)
   * @param {{ x: number, y: number }} params.offset - Canvas offset
   * @param {number} params.zoom - Current zoom level
   * @param {string} params.scaleDirection - Direction of scale (N, S, E, W, NE, NW, SE, SW)
   * @returns {LineElement} New LineElement instance with scaled coordinates
   */
  resize({
    scale,
    anchorPoint,
    offset,
    zoom,
    scaleDirection,
    differenceFromInitialOffset,
    dragItem
  }) {
    // Convert anchor point from screen coordinates to design coordinates
    const anchorX = (anchorPoint.x - offset.x) / zoom;
    const anchorY = (anchorPoint.y - offset.y) / zoom;

    // Scale the line coordinates relative to the anchor point
    const newCoordinates = scaleLineCoordinates(
      this.coordinates,
      anchorX,
      anchorY,
      scale
    );

    // Scale thickness proportionally
    const newThickness = (this.thickness || 1) * scale;

    // Calculate new bounding box from scaled coordinates using scaled thickness
    const padding = LineElement.calculatePadding(newThickness, this.endcap);
    const bbox = getLineBoundingBox(
      newCoordinates.x1,
      newCoordinates.y1,
      newCoordinates.x2,
      newCoordinates.y2,
      padding
    );

    return new this.constructor({
      ...this,
      coordinates: newCoordinates,
      thickness: newThickness,
      top: bbox.top,
      left: bbox.left,
      width: bbox.width,
      height: bbox.height
    });
  }

  /**
   * Get snap points for this line element.
   * Returns endpoints and center for snapping during drag operations.
   * @returns {{ x: object, y: object }} Snap point coordinates
   */
  getSnapPoints() {
    const { x1, y1, x2, y2 } = this.coordinates;
    const centerPoint = calculateLineCenter(x1, y1, x2, y2);
    const bbox = this.boundingBox;

    // For lines, we want to snap:
    // - Start endpoint (x1, y1)
    // - End endpoint (x2, y2)
    // - Center point
    // - Bounding box edges
    return {
      x: {
        left: bbox.left,
        center: centerPoint.x,
        right: bbox.left + bbox.width,
        startPoint: x1,
        endPoint: x2
      },
      y: {
        top: bbox.top,
        center: centerPoint.y,
        bottom: bbox.top + bbox.height,
        startPoint: y1,
        endPoint: y2
      }
    };
  }

  /**
   * Get snap points with offsets for this line element.
   * Used by the snap system during drag operations.
   * @returns {{ x: object, y: object, offsets: object }}
   */
  getSnapPointsWithOffsets() {
    const snapPoints = this.getSnapPoints();
    const bbox = this.boundingBox;

    const horizontalOffsets = {
      left: bbox.left - snapPoints.x.left,
      center: bbox.left - snapPoints.x.center,
      right: bbox.left - snapPoints.x.right
    };

    const verticalOffsets = {
      top: bbox.top - snapPoints.y.top,
      center: bbox.top - snapPoints.y.center,
      bottom: bbox.top - snapPoints.y.bottom
    };

    return {
      ...snapPoints,
      offsets: {
        x: horizontalOffsets,
        y: verticalOffsets
      }
    };
  }
}

export default LineElement;
